import { LinkPill } from '@/components/EditorPill'
import { UserPlusIcon } from '@ttab/elephant-ui/icons'
import { useAuthors } from '@/hooks'
import { useBylineOrganisation } from '@/hooks/useBylineOrganisation'
import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks'
import type { Block } from '@ttab/elephant-api/newsdoc'
import type { JSX } from 'react'
import type * as Y from 'yjs'
import { useTranslation } from 'react-i18next'
import { BylineCreditToggle } from './BylineCreditToggle'
import { BYLINE_CREDIT_PATH, bylineCredit, creditsOrganisation } from './lib/bylineCredit'

const BYLINE_PATH = 'links.core/author'

/**
 * Byline on the editor surface itself, above the text, so it is part of what
 * you look at while writing and it is obvious when it is empty. Nothing is
 * ever prefilled, setting it is always a deliberate act by the author.
 *
 * Where the organisation can be credited, the pill reads the way the article
 * is published: the organisation first, the authors muted after it.
 */
export const EditorByline = ({ ydoc, readOnly }: {
  ydoc: YDocument<Y.Map<unknown>>
  readOnly?: boolean
}): JSX.Element => {
  const { t } = useTranslation('metaSheet')
  const [credit, setCredit] = useYValue<Block[] | undefined>(ydoc.ele, BYLINE_CREDIT_PATH)
  const options = useAuthors().map((author) => {
    return {
      value: author.id,
      label: author.name
    }
  })

  const organisation = useBylineOrganisation()
  const creditsOrg = !!organisation && creditsOrganisation(credit)

  return (
    <LinkPill
      ydoc={ydoc}
      readOnly={readOnly}
      field='byline'
      path={BYLINE_PATH}
      blockType='core/author'
      rel='author'
      idKey='uuid'
      options={options}
      Icon={UserPlusIcon}
      addLabel={t('placeholders.addByline')}
      emptyLabel={creditsOrg ? '' : t('misc.noByline')}
      lead={creditsOrg ? organisation : undefined}
      hint={creditsOrg ? t('bylineCredit.authorsHint') : undefined}
      action={!!organisation && !readOnly
        ? (
            <BylineCreditToggle
              organisation={organisation}
              pressed={creditsOrg}
              onPressedChange={(pressed) => { setCredit(bylineCredit(pressed)) }}
            />
          )
        : undefined}
    />
  )
}
